import React from "react";
import { cn } from "../lib/utils";
import { CheckCircle2, Circle, ChevronRight } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";

/**
 * Minimalist horizontal timeline component for task progress
 * Shows stages as dots connected by lines with tooltips
 * Can be interactive (clickable) for managers to advance stages
 */
const TaskTimeline = ({ 
  stages = [], 
  currentStageIndex = 0,
  showLabels = true,
  size = "default", // "sm" | "default" | "lg"
  className = "",
  onStageClick = null, // (stageId, stageIndex) => void - any stage but the current one
  interactive = false, // Enable clicks on stages
  pendingIndex = null, // A clicked stage waiting for confirmation — outlined
  showTooltips = true, // Show tooltips on hover
}) => {
  if (!stages || stages.length === 0) {
    return null;
  }

  const sizeClasses = {
    sm: {
      dot: "w-3 h-3",
      line: "h-0.5",
      text: "text-[9px]",
      gap: "gap-1"
    },
    default: {
      dot: "w-4 h-4",
      line: "h-0.5",
      text: "text-[10px]",
      gap: "gap-2"
    },
    lg: {
      dot: "w-5 h-5",
      line: "h-1",
      text: "text-xs",
      gap: "gap-3"
    }
  };

  const s = sizeClasses[size] || sizeClasses.default;

  // Get status label for tooltip
  const getStageStatusLabel = (stage, index) => {
    if (index < currentStageIndex) return "Completed";
    if (index === currentStageIndex) return "In Progress";
    return "Pending";
  };

  // Build tooltip content
  const getTooltipContent = (stage, index) => {
    const statusLabel = getStageStatusLabel(stage, index);
    const description = stage.description;
    
    return (
      <div className="space-y-1">
        <div className="font-medium">{stage.name}</div>
        <div className="text-xs opacity-80">{statusLabel}</div>
        {description && (
          <div className="text-xs opacity-70 max-w-[200px]">{description}</div>
        )}
        {index !== currentStageIndex && interactive && onStageClick && (
          <div className="text-xs text-orange-300 mt-1">
            {index < currentStageIndex ? "Click to move back here" : "Click to move here"}
          </div>
        )}
      </div>
    );
  };

  const renderStageDot = (stage, index) => {
    const isCompleted = index < currentStageIndex;
    const isCurrent = index === currentStageIndex;
    const isPending = index === pendingIndex;
    const canClick = interactive && !isCurrent && onStageClick;

    return (
      <div
        className={cn(
          s.dot,
          "rounded-full flex items-center justify-center transition-all duration-300",
          isCompleted 
            ? "bg-green-500" 
            : isCurrent 
              ? "bg-orange-500 ring-2 ring-orange-500/30" 
              : isPending
                ? "bg-orange-500/20 border border-dashed border-orange-500 cursor-pointer"
                : canClick
                  ? "bg-zinc-700 border border-zinc-600 hover:border-orange-500 hover:bg-orange-500/20 cursor-pointer"
                  : "bg-zinc-700 border border-zinc-600",
          isCompleted && canClick && "cursor-pointer hover:ring-2 hover:ring-orange-500/40"
        )}
        style={isCompleted || isCurrent ? { backgroundColor: stage.color || (isCurrent ? "#f97316" : "#22c55e") } : {}}
        onClick={canClick ? () => onStageClick(stage.id, index) : undefined}
      >
        {isCompleted && (
          <CheckCircle2 className="w-2.5 h-2.5 text-zinc-50" />
        )}
        {isCurrent && (
          <Circle className="w-1.5 h-1.5 text-zinc-50 fill-white" />
        )}
        {canClick && !isCompleted && index === currentStageIndex + 1 && (
          <ChevronRight className="w-2 h-2 text-zinc-400" />
        )}
      </div>
    );
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div className={cn("flex flex-col", className)}>
        {/* Timeline visualization */}
        <div className="flex items-center w-full">
          {stages.map((stage, index) => {
            const isCompleted = index < currentStageIndex;
            const isCurrent = index === currentStageIndex;
            const isLast = index === stages.length - 1;
            const canClick = interactive && !isCurrent && onStageClick;

            return (
              <React.Fragment key={stage.id || index}>
                {/* Stage dot with tooltip */}
                <div className="flex flex-col items-center">
                  {showTooltips ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        {renderStageDot(stage, index)}
                      </TooltipTrigger>
                      <TooltipContent 
                        side="top" 
                        className="bg-zinc-800 border border-zinc-700 text-zinc-100"
                      >
                        {getTooltipContent(stage, index)}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    renderStageDot(stage, index)
                  )}
                  
                  {/* Stage label */}
                  {showLabels && (
                    <span 
                      className={cn(
                        s.text, 
                        "mt-1 text-center max-w-[60px] truncate",
                        isCompleted 
                          ? "text-green-400" 
                          : isCurrent 
                            ? "text-orange-400 font-medium" 
                            : index === pendingIndex
                              ? "text-orange-300"
                              : canClick
                                ? "text-zinc-400 cursor-pointer hover:text-orange-400"
                                : "text-zinc-500",
                        isCompleted && canClick && "cursor-pointer"
                      )}
                      onClick={canClick ? () => onStageClick(stage.id, index) : undefined}
                    >
                      {stage.name}
                    </span>
                  )}
                </div>

                {/* Connector line */}
                {!isLast && (
                  <div 
                    className={cn(
                      "flex-1 mx-1",
                      s.line,
                      "rounded-full transition-all duration-300",
                      isCompleted 
                        ? "bg-green-500" 
                        : "bg-zinc-700"
                    )}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </TooltipProvider>
  );
};

/**
 * Compact timeline pill showing current stage
 */
export const TimelinePill = ({ 
  currentStage, 
  totalStages, 
  currentIndex,
  color = "#f97316" 
}) => {
  if (!currentStage) return null;

  return (
    <div className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-zinc-800 rounded-full border border-zinc-700">
      <div 
        className="w-2 h-2 rounded-full"
        style={{ backgroundColor: color }}
      />
      <span className="text-[10px] text-zinc-300 font-medium">
        {currentStage}
      </span>
      <span className="text-[10px] text-zinc-500">
        ({currentIndex + 1}/{totalStages})
      </span>
    </div>
  );
};

/**
 * Timeline progress bar (alternative compact view)
 */
export const TimelineProgressBar = ({ 
  currentIndex = 0, 
  totalStages = 1,
  showPercentage = false,
  className = ""
}) => {
  const percentage = totalStages > 0 ? ((currentIndex + 1) / totalStages) * 100 : 0;

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-orange-500 to-green-500 rounded-full transition-all duration-500"
          style={{ width: `${percentage}%` }}
        />
      </div>
      {showPercentage && (
        <span className="text-[10px] text-zinc-500 w-8">
          {Math.round(percentage)}%
        </span>
      )}
    </div>
  );
};

export default TaskTimeline;
